/**
 * Bounded-concurrency video loader.
 * Hard cap: ≤ 2 decoded HTMLVideoElement instances alive at once.
 * Dispose = clear src + load() to release decoder memory.
 */

export const MAX_DECODED_VIDEOS = 2;
export const DEFAULT_CONCURRENCY = 2;
export const DEFAULT_RETRIES = 2;

export interface LoaderOptions {
  /** Max parallel network/fetches for assigning src. Default 2. */
  concurrency?: number;
  /** Retries after failure (not counting first attempt). Default 2. */
  retries?: number;
  /** Optional fetch-like probe before assign (tests inject mocks). */
  fetchImpl?: typeof fetch;
  /** Max decoded videos alive at once. Default MAX_DECODED_VIDEOS (2). */
  maxAlive?: number;
}

export type LoadState = 'idle' | 'loading' | 'ready' | 'error' | 'disposed';

export interface ManagedVideo {
  id: string;
  el: HTMLVideoElement;
  state: LoadState;
  url: string | null;
}

interface QueueItem {
  id: string;
  url: string;
  el: HTMLVideoElement;
  resolve: (el: HTMLVideoElement) => void;
  reject: (err: Error) => void;
  signal?: AbortSignal;
  attempt: number;
  settled: boolean;
  controller: AbortController;
}

export class VideoLoader {
  readonly maxAlive: number;
  /** Id of the actively-scrubbed scene — never evicted by other loads. */
  private protectedId: string | null = null;
  private readonly concurrency: number;
  private readonly retries: number;
  private readonly fetchImpl: typeof fetch | undefined;
  private readonly alive = new Map<string, ManagedVideo>();
  private readonly queue: QueueItem[] = [];
  /** In-flight jobs keyed by id (for cancel). */
  private readonly inflight = new Map<string, QueueItem>();
  /** Unsettled load promises keyed by id, so re-entrant loads can join. */
  private readonly pending = new Map<string, Promise<HTMLVideoElement>>();
  private activeJobs = 0;
  private seq = 0;

  constructor(options: LoaderOptions = {}) {
    this.concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
    this.retries = options.retries ?? DEFAULT_RETRIES;
    this.fetchImpl = options.fetchImpl;
    this.maxAlive = options.maxAlive ?? MAX_DECODED_VIDEOS;
  }

  /**
   * Mark one id as the active scene: eviction skips it so a neighbouring
   * chapter's prefetch can never dispose the video being scrubbed/played.
   */
  protect(id: string | null): void {
    this.protectedId = id;
  }

  getProtected(): string | null {
    return this.protectedId;
  }

  getAliveCount(): number {
    return this.alive.size;
  }

  getAliveIds(): string[] {
    return [...this.alive.keys()];
  }

  getManaged(id: string): ManagedVideo | undefined {
    return this.alive.get(id);
  }

  /**
   * Register / load a video URL into `el`. Evicts oldest if cap exceeded.
   * Bounded concurrency across the loader instance.
   */
  load(
    url: string,
    el: HTMLVideoElement,
    opts: { id?: string; signal?: AbortSignal } = {},
  ): Promise<HTMLVideoElement> {
    const id = opts.id ?? `vid-${++this.seq}`;

    // Re-entrant load of the same id+el+url while still loading: join the
    // in-flight promise. Disposing here would reject the first caller's
    // await (the "Load disposed" uncaught seen when onEnter/onEnterBack
    // both request the same scene).
    const current = this.alive.get(id);
    const inFlight = this.pending.get(id);
    if (
      current &&
      current.el === el &&
      current.url === url &&
      current.state === 'loading' &&
      inFlight
    ) {
      return inFlight;
    }

    // Reuse existing slot for same element
    for (const [existingId, managed] of this.alive) {
      if (managed.el === el) {
        if (managed.url === url && managed.state === 'ready') {
          return Promise.resolve(el);
        }
        this.disposeById(existingId);
        break;
      }
    }

    while (this.alive.size >= this.maxAlive) {
      const evictable = [...this.alive.keys()].find(
        (k) => k !== this.protectedId && k !== id,
      );
      if (evictable === undefined) break;
      this.disposeById(evictable);
    }

    const managed: ManagedVideo = {
      id,
      el,
      state: 'loading',
      url,
    };
    this.alive.set(id, managed);

    const controller = new AbortController();
    if (opts.signal) {
      if (opts.signal.aborted) {
        controller.abort();
      } else {
        opts.signal.addEventListener('abort', () => controller.abort(), {
          once: true,
        });
      }
    }

    const promise = new Promise<HTMLVideoElement>((resolve, reject) => {
      this.queue.push({
        id,
        url,
        el,
        resolve,
        reject,
        signal: controller.signal,
        attempt: 0,
        settled: false,
        controller,
      });
      this.pump();
    });
    this.pending.set(id, promise);
    const clear = (): void => {
      if (this.pending.get(id) === promise) this.pending.delete(id);
    };
    promise.then(clear, clear);
    return promise;
  }

  /**
   * Cancel a pending/in-flight load by id. Disposes if already attached.
   */
  cancel(id: string): void {
    const idx = this.queue.findIndex((q) => q.id === id);
    if (idx >= 0) {
      const [item] = this.queue.splice(idx, 1);
      this.settleReject(item, new Error(`Load cancelled: ${id}`));
    }
    const active = this.inflight.get(id);
    if (active) {
      active.controller.abort();
      this.settleReject(active, new Error(`Load cancelled: ${id}`));
    }
    if (this.alive.has(id)) {
      this.disposeById(id);
    }
  }

  /**
   * Dispose = clear src + load() to drop decoder memory.
   */
  dispose(el: HTMLVideoElement): void {
    for (const [id, managed] of this.alive) {
      if (managed.el === el) {
        this.disposeById(id);
        return;
      }
    }
    clearVideoElement(el);
  }

  disposeById(id: string): void {
    const active = this.inflight.get(id);
    if (active) {
      active.controller.abort();
      this.settleReject(active, new Error(`Load disposed: ${id}`));
    }
    const managed = this.alive.get(id);
    if (!managed) return;
    clearVideoElement(managed.el);
    managed.state = 'disposed';
    managed.url = null;
    this.alive.delete(id);
  }

  disposeAll(): void {
    for (const id of [...this.alive.keys()]) {
      this.disposeById(id);
    }
    while (this.queue.length) {
      const item = this.queue.shift();
      if (item) this.settleReject(item, new Error('Loader disposed'));
    }
    for (const [id, item] of this.inflight) {
      item.controller.abort();
      this.settleReject(item, new Error('Loader disposed'));
      this.inflight.delete(id);
    }
  }

  private settleReject(item: QueueItem, err: Error): void {
    if (item.settled) return;
    item.settled = true;
    item.reject(err);
  }

  private settleResolve(item: QueueItem, el: HTMLVideoElement): void {
    if (item.settled) return;
    item.settled = true;
    item.resolve(el);
  }

  private pump(): void {
    while (this.activeJobs < this.concurrency && this.queue.length > 0) {
      const item = this.queue.shift();
      if (!item) break;
      if (item.settled || item.signal?.aborted) {
        this.settleReject(item, new Error(`Load aborted: ${item.id}`));
        this.alive.delete(item.id);
        continue;
      }
      this.activeJobs += 1;
      this.inflight.set(item.id, item);
      void this.runJob(item).finally(() => {
        this.inflight.delete(item.id);
        this.activeJobs -= 1;
        this.pump();
      });
    }
  }

  private async runJob(item: QueueItem): Promise<void> {
    const managed = this.alive.get(item.id);
    if (!managed) {
      this.settleReject(item, new Error(`Unknown load id: ${item.id}`));
      return;
    }

    try {
      if (this.fetchImpl) {
        const res = await this.fetchImpl(item.url, { signal: item.signal });
        if (item.settled) return;
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} for ${item.url}`);
        }
      }

      if (item.settled || item.signal?.aborted) {
        this.settleReject(item, new Error(`Load aborted: ${item.id}`));
        return;
      }

      await assignVideoSrc(item.el, item.url, item.signal);
      if (item.settled) return;
      managed.state = 'ready';
      managed.url = item.url;
      this.settleResolve(item, item.el);
    } catch (err) {
      if (item.settled) return;
      if (item.attempt < this.retries && !item.signal?.aborted) {
        item.attempt += 1;
        this.queue.unshift(item);
        return;
      }
      managed.state = 'error';
      this.settleReject(
        item,
        err instanceof Error ? err : new Error(String(err)),
      );
    }
  }
}

export function clearVideoElement(el: HTMLVideoElement): void {
  try {
    el.pause();
  } catch {
    /* jsdom may not implement pause */
  }
  el.removeAttribute('src');
  // Keep property empty; jsdom may retain attribute as ""
  try {
    el.src = '';
  } catch {
    /* ignore */
  }
  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }
  try {
    el.load();
  } catch {
    /* jsdom may not implement load */
  }
}

function assignVideoSrc(
  el: HTMLVideoElement,
  url: string,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('aborted'));
      return;
    }

    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Failed to load video: ${url}`));
    };
    const onAbort = () => {
      cleanup();
      clearVideoElement(el);
      reject(new Error('aborted'));
    };

    const cleanup = () => {
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
    };

    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('error', onError);
    signal?.addEventListener('abort', onAbort, { once: true });

    el.src = url;
    try {
      el.load();
    } catch {
      /* jsdom */
    }
  });
}

/** Singleton for app use; tests construct VideoLoader directly. */
export const videoLoader = new VideoLoader();
